import { withDeveloperBenchmark } from "./scoring.js";

const LEADERBOARD_FALLBACK_KEY = "orbitrush_leaderboard_fallback_v1";
const PROFILE_KEY = "orbitrush_profile_v1";
const CLOUD_META_KEY = "orbitrush_cloud_meta_v1";
const GAME_SAVE_KEY = "orbitrush_save_v2";
const FORCE_CLOUD_PUSH_KEY = "orbitrush_force_cloud_push_v1";

function safeJsonParse(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function randomToken(length = 6) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const values = new Uint8Array(length);
  crypto.getRandomValues(values);
  return [...values].map((value) => chars[value % chars.length]).join("");
}

export function getProfile() {
  const existing = safeJsonParse(localStorage.getItem(PROFILE_KEY), null);
  if (existing?.id && existing?.name) return existing;
  const profile = {
    id: crypto.randomUUID?.() || `pilot-${Date.now()}-${randomToken(8)}`,
    name: `PILOT-${randomToken(4)}`,
    customized: false,
  };
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  return profile;
}

export function hasChosenPilotName() {
  return Boolean(getProfile().customized);
}

export function normalizePilotName(name) {
  return String(name || "")
    .toUpperCase()
    .replace(/[^A-Z0-9 _-]/g, "")
    .trim()
    .slice(0, 16);
}

export function setPilotName(name) {
  const profile = getProfile();
  const clean = normalizePilotName(name);
  if (clean.length < 2) return profile;
  const updated = { ...profile, name: clean, customized: true };
  localStorage.setItem(PROFILE_KEY, JSON.stringify(updated));
  return updated;
}

function getFallbackBoards() {
  return safeJsonParse(localStorage.getItem(LEADERBOARD_FALLBACK_KEY), {});
}

function addFallbackScore(entry) {
  const boards = getFallbackBoards();
  const list = Array.isArray(boards[entry.roadId]) ? boards[entry.roadId] : [];
  list.push({ ...entry, source: "local" });
  list.sort((a, b) => a.time - b.time);
  boards[entry.roadId] = list.slice(0, 20);
  localStorage.setItem(LEADERBOARD_FALLBACK_KEY, JSON.stringify(boards));
  return boards[entry.roadId];
}

async function apiFetch(path, options = {}) {
  if (location.protocol === "file:") {
    throw new Error("The online service needs the Orbitrush server.");
  }
  const response = await fetch(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    throw new Error(`Orbitrush service returned ${response.status}.`);
  }
  return response.json();
}

export async function submitScore({ roadId, time, stars }) {
  const profile = getProfile();
  const entry = {
    roadId,
    time: Number(time.toFixed(3)),
    stars,
    name: profile.name,
    playerId: profile.id,
  };

  addFallbackScore(entry);
  try {
    const result = await apiFetch("/api/leaderboards", {
      method: "POST",
      body: JSON.stringify(entry),
    });
    return { ...result, online: true };
  } catch (error) {
    const entries = withDeveloperBenchmark(
      roadId,
      getFallbackBoards()[roadId] || [],
    );
    return {
      online: false,
      rank: entries.findIndex((score) => score.playerId === profile.id) + 1,
      entries,
      message: error.message,
    };
  }
}

export async function getLeaderboard(roadId) {
  try {
    const result = await apiFetch(
      `/api/leaderboards/${encodeURIComponent(roadId)}`,
    );
    return { ...result, online: true };
  } catch (error) {
    return {
      roadId,
      entries: withDeveloperBenchmark(
        roadId,
        getFallbackBoards()[roadId] || [],
      ),
      online: false,
      message: error.message,
    };
  }
}

export function startCloudBackup(onRestore) {
  const profile = getProfile();
  let lastSave = localStorage.getItem(FORCE_CLOUD_PUSH_KEY)
    ? null
    : localStorage.getItem(GAME_SAVE_KEY);
  let stopped = false;

  const restore = async () => {
    if (lastSave) return;
    try {
      const remote = await apiFetch(`/api/save/${encodeURIComponent(profile.id)}`);
      if (remote.save && !localStorage.getItem(GAME_SAVE_KEY)) {
        localStorage.setItem(GAME_SAVE_KEY, JSON.stringify(remote.save));
        localStorage.setItem(
          CLOUD_META_KEY,
          JSON.stringify({ restoredAt: Date.now(), updatedAt: remote.updatedAt }),
        );
        onRestore?.();
      }
    } catch {
      // Offline play remains fully functional.
    }
  };

  const backup = async () => {
    if (stopped) return;
    const current = localStorage.getItem(GAME_SAVE_KEY);
    if (!current || current === lastSave) return;
    lastSave = current;
    try {
      const parsed = safeJsonParse(current, null);
      if (!parsed) return;
      const result = await apiFetch(`/api/save/${encodeURIComponent(profile.id)}`, {
        method: "PUT",
        body: JSON.stringify({ save: parsed }),
      });
      localStorage.setItem(
        CLOUD_META_KEY,
        JSON.stringify({ backedUpAt: Date.now(), updatedAt: result.updatedAt }),
      );
      localStorage.removeItem(FORCE_CLOUD_PUSH_KEY);
    } catch {
      // The next interval retries after another local save change.
    }
  };

  restore();
  const interval = window.setInterval(backup, 8_000);
  return () => {
    stopped = true;
    clearInterval(interval);
  };
}
